/* Surisa desktop megamenu — gestiona apertura/cierre del dropdown rail+panel
   y el cambio de tab interior. Acompaña a snippets/header-megamenu.liquid y
   a sections/header.liquid (que envuelve la <header> en <header-megamenu>).

   Comportamiento:
   - hover sobre un trigger (.surisa-header__nav-link--trigger) → abre con 80ms de delay
   - mouseleave del header → cierra con 120ms de delay (evita parpadeos al pasar al panel)
   - focus en el trigger → abre inmediatamente (accesibilidad teclado)
   - Esc → cierra
   - hover sobre cada tab → switchea el panel visible (replica el patrón del diseño)
*/

(function () {
  var OPEN_DELAY  = 80;
  var CLOSE_DELAY = 120;

  function isDesktop() {
    return window.matchMedia('(min-width: 990px)').matches;
  }

  class HeaderMegamenu extends HTMLElement {
    constructor() {
      super();
      this.openTimer  = null;
      this.closeTimer = null;
      this.currentHandle = null;
    }

    connectedCallback() {
      this.triggers = Array.from(this.querySelectorAll('[data-megamenu-trigger]'));
      this.menus    = Array.from(this.querySelectorAll('[data-megamenu-handle]'));

      if (this.triggers.length === 0 || this.menus.length === 0) return;

      this.onKeydown = this.onKeydown.bind(this);
      this.onHeaderMouseLeave = this.onHeaderMouseLeave.bind(this);

      this.triggers.forEach((trigger) => {
        const handle = trigger.dataset.megamenuTrigger;
        const li = trigger.closest('.surisa-header__nav-item');

        if (li) {
          li.addEventListener('mouseenter', () => {
            if (!isDesktop()) return;
            this.cancelClose();
            this.scheduleOpen(handle);
          });
          li.addEventListener('mouseleave', () => {
            if (!isDesktop()) return;
            this.cancelOpen();
            this.scheduleClose();
          });
        }
        trigger.addEventListener('focus', () => {
          if (!isDesktop()) return;
          this.openNow(handle);
        });
      });

      this.menus.forEach((menu) => {
        menu.addEventListener('mouseenter', () => this.cancelClose());
        menu.addEventListener('mouseleave', () => this.scheduleClose());

        const tabs = menu.querySelectorAll('[data-megamenu-tab]');
        tabs.forEach((tab) => {
          tab.addEventListener('mouseenter', () => this.switchTab(menu, tab.dataset.megamenuTab));
          tab.addEventListener('focus',      () => this.switchTab(menu, tab.dataset.megamenuTab));
          tab.addEventListener('click', (e) => {
            /* tabs are <button type="button"> — prevent any form-like default */
            e.preventDefault();
            this.switchTab(menu, tab.dataset.megamenuTab);
          });
        });

        /* close on focusout if focus leaves the whole header */
        menu.addEventListener('focusout', (e) => {
          if (!isDesktop()) return;
          if (!this.contains(e.relatedTarget)) this.closeNow();
        });
      });

      this.addEventListener('mouseleave', this.onHeaderMouseLeave);
      document.addEventListener('keydown', this.onKeydown);
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKeydown);
      this.removeEventListener('mouseleave', this.onHeaderMouseLeave);
      this.cancelOpen();
      this.cancelClose();
    }

    scheduleOpen(handle) {
      this.cancelOpen();
      this.openTimer = setTimeout(() => this.openNow(handle), OPEN_DELAY);
    }
    cancelOpen() {
      if (this.openTimer) { clearTimeout(this.openTimer); this.openTimer = null; }
    }

    scheduleClose() {
      this.cancelClose();
      this.closeTimer = setTimeout(() => this.closeNow(), CLOSE_DELAY);
    }
    cancelClose() {
      if (this.closeTimer) { clearTimeout(this.closeTimer); this.closeTimer = null; }
    }

    openNow(handle) {
      if (this.currentHandle === handle) return;
      /* close any other open menu first */
      if (this.currentHandle) this.hideMenu(this.currentHandle);
      this.showMenu(handle);
      this.currentHandle = handle;
    }

    closeNow() {
      if (!this.currentHandle) return;
      this.hideMenu(this.currentHandle);
      this.currentHandle = null;
    }

    showMenu(handle) {
      const menu = this.menus.find((m) => m.dataset.megamenuHandle === handle);
      if (!menu) return;
      menu.hidden = false;
      /* allow next paint for transition to engage */
      requestAnimationFrame(() => menu.setAttribute('data-open', 'true'));
      this.triggers.forEach((t) => {
        if (t.dataset.megamenuTrigger === handle) t.setAttribute('aria-expanded', 'true');
      });
    }

    hideMenu(handle) {
      const menu = this.menus.find((m) => m.dataset.megamenuHandle === handle);
      if (!menu) return;
      menu.removeAttribute('data-open');
      setTimeout(() => {
        /* only hide if it hasn't been re-opened in the meantime */
        if (!menu.hasAttribute('data-open')) menu.hidden = true;
      }, 200);
      this.triggers.forEach((t) => {
        if (t.dataset.megamenuTrigger === handle) t.setAttribute('aria-expanded', 'false');
      });
    }

    switchTab(menu, tabId) {
      const tabs = menu.querySelectorAll('[data-megamenu-tab]');
      tabs.forEach((t) => {
        const active = t.dataset.megamenuTab === tabId;
        t.classList.toggle('is-active', active);
        t.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      const panes = menu.querySelectorAll('[data-megamenu-panel]');
      panes.forEach((p) => {
        const active = p.dataset.megamenuPanel === tabId;
        p.classList.toggle('is-active', active);
        p.hidden = !active;
      });
    }

    onHeaderMouseLeave() {
      if (!isDesktop()) return;
      this.scheduleClose();
    }

    onKeydown(event) {
      if (event.key === 'Escape' && this.currentHandle) {
        event.preventDefault();
        this.closeNow();
        /* return focus to the trigger that opened it */
        const trigger = this.triggers.find((t) => t.dataset.megamenuTrigger === this.currentHandle);
        if (trigger) trigger.focus();
      }
    }
  }

  customElements.define('header-megamenu', HeaderMegamenu);
})();
